'use client';

import { useScreenDetect } from '@sgcmpteltd/uikit-next';
import {
  Button,
  DeleteCircleContained,
  Filter,
  Input,
  Magnifier,
  Select,
  useDocumentClick,
  useLatest,
} from '@sgcmpteltd/uikit-react';
import {
  MENU_BORDER_RADIUS_EXTRA_LARGE,
  MENU_FIT_CONTENT,
} from '@sgcmpteltd/uikit-react/components/select';
import {
  alignBottomXBegin,
  alignBottomXEnd,
} from '@sgcmpteltd/uikit-react/utils/position/absolute';
import cx from 'clsx';
import type {
  ChangeEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
} from 'react';
import { useRef, useState } from 'react';

import { DualSelect } from '@/components/dual-select';
import { AutoCompleteQuickBox } from '@/components/search-filter/auto-complete';
import {
  FILTER_TYPE_NAME_DEPRECIATION,
  FILTER_TYPE_NAME_PRICE_RANGE,
} from '@/constants/global';

import {
  depreciationRangeSelectOptionList,
  priceRangeSelectOptionList,
} from '../configs';
import { CAR_MAKE_SELECT_PLACEHOLDER_TEXT } from '../constants';
import type { FilterRange } from '../filter-label-list/types';
import {
  categorySelectOptionConfigs,
  vehicleTypeSelectOptionConfigs,
} from './configs';
import { BUTTON_SEARCH_TEXT, FILTER_ICON_SIZE } from './constants';
import { Select as SpecialSelect } from './select';
import styles from './styles.module.scss';
import type { Component } from './types';

export const SearchBar: Component = ({
  autoCompleteOptions,
  brandList,
  searchValue,
  setSearchValue,
  carMakeValue,
  selectValues,
  setSelectValues,
  rangeValues,
  setRangeValues,
  checkboxValues,
  setCheckboxValues,
  submitButtonOption,
  promotionOptions,
  searchInputOption,
  selectOptions,
  isAutoCompleteOpen,
  simpleMode,
  className,
  showPromotions = false,
  onAutoCompleteBoxOpen,
  onIgnoreSearchInputChange,
  onSearchInputChange,
  onCarMakeChange,
  onFilterClick,
  onSubmit,
  numberOfAppliedFilters = 0,
}) => {
  const { isIpadMini, isDesktop } = useScreenDetect();
  const isIpadMiniMode = isIpadMini();
  const isDesktopMode = isDesktop();

  const [isCarMakeMenuOpen, setIsCarMakeMenuOpen] = useState(false);
  const [isVehicleTypeMenuOpen, setIsVehicleTypeMenuOpen] = useState(false);

  const handleFilterClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    onFilterClick?.(e);
  };

  const handleSearchInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    onSearchInputChange?.(e.target.value);
  };

  const handleSearchInputChangeFromAutoComplete = (optionValue: string) => {
    onSearchInputChange?.(optionValue);
    onAutoCompleteBoxOpen?.(false);
  };

  const handleSearchClick: MouseEventHandler<HTMLButtonElement> = () => {
    onSubmit?.();
  };

  const handleSubmit: KeyboardEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    onSubmit?.();
  };

  const handleRangeClear = (name: string) => () => {
    setRangeValues({
      ...rangeValues,
      [name]: {
        name,
        type: 'range',
        lowerValue: {
          text: 'Lowest',
          value: '',
        },
        upperValue: {
          text: 'Highest',
          value: '',
        },
      },
    });
  };

  const handleRangeChange = (v: FilterRange) => {
    setRangeValues({
      ...rangeValues,
      [v.name]: {
        name: v.name,
        type: 'range',
        lowerValue: v.lowerValue,
        upperValue: v.upperValue,
      },
    });
  };

  const buttonText = (v: FilterRange) => {
    const { name, lowerValue, upperValue } = v;

    if (lowerValue.value && upperValue.value) {
      return `${lowerValue.text} to ${upperValue.text}`;
    }

    if (lowerValue.value && !upperValue.value) {
      return `More than ${lowerValue.text}`;
    }

    if (!lowerValue.value && upperValue.value) {
      return `Less than ${upperValue.text}`;
    }
    return name;
  };

  const handleCarMakeChange = (value: string | number) => {
    onCarMakeChange?.(value.toString());
  };

  const handleMenuOpenChange = (isOpen: boolean) => {
    setIsCarMakeMenuOpen(isOpen);
  };

  const handleVehicleTypeMenuOpenChange = (isOpen: boolean) => {
    setIsVehicleTypeMenuOpen(isOpen);
  };

  const inputElRef = useRef<HTMLInputElement | null>(null);

  const {
    ref: onDocumentClickRef,
  } = useLatest<Document['onclick']>({
    value: ({ target }) => {
      const { current: searchInputField } = inputElRef;

      if (
        !searchInputField
        || !(target instanceof HTMLElement)
        || !document.documentElement.contains(target)
        || searchInputField.contains(target)
      ) {
        return;
      }

      if (onAutoCompleteBoxOpen) {
        onAutoCompleteBoxOpen(false);
      }
    },
  });

  useDocumentClick({
    onClickRef: onDocumentClickRef,
  });

  const handleVehicleTypeSelectChange = (value: string | number) => {
    const newValues = { ...checkboxValues['Vehicle Type'].values };

    for (const vehicleType in newValues) {
      if (Object.hasOwn(newValues, vehicleType)) {
        newValues[vehicleType].checked = false;
      }
    }

    setCheckboxValues({
      ...checkboxValues,
      'Vehicle Type': {
        name: 'Vehicle Type',
        type: 'checkbox',
        values: {
          ...newValues,
          [vehicleTypeSelectOptionConfigs.optionList
            .find(option => option.value === value)?.text ?? '']: {
            name: vehicleTypeSelectOptionConfigs.optionList
              .find(option => option.value === value)?.text ?? '',
            value,
            checked: true,
          },
        },
      },
    });
  };

  const [isFocused, setIsFocused] = useState(false);

  const handleInputSearchOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);

    if (searchInputOption?.onFocus) {
      searchInputOption.onFocus(e);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSubmit?.();
    }

    if (searchInputOption?.onKeyDown) {
      searchInputOption?.onKeyDown(e);
    }
  };

  const handleInputSearchOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);

    if (searchInputOption?.onBlur) {
      searchInputOption.onBlur(e);
    }
  };

  const containerRef = useRef<HTMLDivElement>(null);

  const handleToScrollToTop = () => {
    setTimeout(() => {
      if (containerRef.current && !isDesktopMode) {
        const offset = isDesktopMode ? 45 : 130;
        const elementPosition = containerRef.current
          .getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - offset;

        window.scrollTo({ left: 0, top: offsetPosition, behavior: 'smooth' });
      }
    }, 0);
  };

  return (
    <form
      className={cx(
        styles.containerRoot,
        simpleMode && styles.isSimpleMode,
        className,
      )}
      onSubmit={handleSubmit}>
      <div
        className={styles.containerSearch}
        style={{ gridArea: 'a' }}>
        <div className={styles.containerInput}>
          <Input
            ref={inputElRef}
            alt={searchInputOption?.alt}
            aria-label={searchInputOption?.['aria-label']}
            autoComplete={searchInputOption?.autoComplete}
            className={cx(
              searchInputOption?.className,
              styles.inputSearch,
              styles.isRound,
            )}
            disabled={searchInputOption?.disabled}
            id={searchInputOption?.id}
            inputMode={searchInputOption?.inputMode}
            isBorderless={searchInputOption?.isBorderless}
            isInvalid={searchInputOption?.isInvalid}
            name={searchInputOption?.name}
            pattern={searchInputOption?.pattern}
            placeholder={searchInputOption?.placeholder}
            readOnly={searchInputOption?.readOnly}
            required={searchInputOption?.required}
            step={searchInputOption?.step}
            tabIndex={searchInputOption?.tabIndex}
            type={searchInputOption?.type}
            value={searchValue}
            onBeforeInput={searchInputOption?.onBeforeInput}
            onBlur={handleInputSearchOnBlur}
            onChange={handleSearchInputChange}
            onCopy={searchInputOption?.onCopy}
            onCut={searchInputOption?.onCut}
            onFocus={handleInputSearchOnFocus}
            onInput={searchInputOption?.onInput}
            onKeyDown={handleSearchKeyDown}
            onKeyUp={searchInputOption?.onKeyUp}
            onPaste={searchInputOption?.onPaste} />
          {searchValue && isFocused && (
            <button
              aria-label="Clear search"
              className={cx(
                styles.iconAsideSearchInput,
                styles.btnClear,
              )}
              onClick={() => setSearchValue('')}
              onMouseDown={e => e.preventDefault()}>
              <DeleteCircleContained size={20} />
            </button>
          )}
          { !isDesktopMode && (
            <button
              aria-label="Clear search"
              className={cx(
                styles.iconAsideSearchInput,
              )}
              onClick={handleSearchClick}
              onMouseDown={e => e.preventDefault()}>
              <Magnifier size={20} />
            </button>
          )}
        </div>
        {isAutoCompleteOpen
        && autoCompleteOptions
        && (
          <AutoCompleteQuickBox
            autoCompleteOptions={autoCompleteOptions}
            promotionOptions={
              isDesktopMode
                ? (promotionOptions || null)
                : null
            }
            showPromotions={showPromotions}
            onAutoCompleteOptionClick={handleSearchInputChangeFromAutoComplete}
            onIgnoreSearchInputChange={onIgnoreSearchInputChange} /> // @suppress longLineCheck
        )}
      </div>
      {!simpleMode && (
        <div
          ref={containerRef}
          aria-label="Scroll to top"
          className={cx(
            styles.containerDualSelect,
            styles.containerCarMakeSelect,
          )}
          role="button"
          style={{ gridArea: 'g' }}
          tabIndex={0}
          onClick={handleToScrollToTop}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleToScrollToTop();
            }
          }}>
          <Select
            headerClassName={cx(
              styles.headerPaddingClassName,
              styles.isPadding,
              isCarMakeMenuOpen && styles.isCarMakeMenuOpen,
              carMakeValue !== '' && styles.isActive,
            )}
            menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
            menuClassName={cx(styles.carMakeMenuClassName, styles.isHeight)}
            menuContainerClassName={styles.hasMargin}
            optionList={[
              { text: 'Any', value: '' },
              ...brandList.map(value => ({
                text: value,
                value,
              })),
            ]}
            placeholder={{
              text: CAR_MAKE_SELECT_PLACEHOLDER_TEXT,
              isOptionHidden: true,
            }}
            value={carMakeValue}
            onChange={handleCarMakeChange}
            onMenuOpenChange={handleMenuOpenChange} />
        </div>
      )}
      {!simpleMode && (
        <div
          ref={containerRef}
          aria-label="Scroll to top"
          className={styles.containerDualSelect}
          role="button"
          style={{ gridArea: 'b' }}
          tabIndex={0}
          onClick={handleToScrollToTop}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleToScrollToTop();
            }
          }}>
          <Select
            headerClassName={cx(
              styles.headerPaddingClassName,
              styles.isPadding,
              isVehicleTypeMenuOpen && styles.isCarMakeMenuOpen,
              Object.values(checkboxValues['Vehicle Type'].values)
                .findIndex(type => type.checked) !== -1 && styles.isActive,
            )}
            menuAlignment={isDesktop() ? 'left' : 'right'}
            menuBorderRadius={MENU_BORDER_RADIUS_EXTRA_LARGE}
            menuContainerClassName={styles.hasMargin}
            menuFit={MENU_FIT_CONTENT}
            optionList={vehicleTypeSelectOptionConfigs.optionList}
            placeholder={vehicleTypeSelectOptionConfigs.placeholder}
            value={
              Object.values(checkboxValues['Vehicle Type'].values)
                .find(type => type.checked)?.value as (string | number) ?? ''
            }
            onChange={handleVehicleTypeSelectChange}
            onMenuOpenChange={handleVehicleTypeMenuOpenChange} />
        </div>
      )}
      {!simpleMode && (
        <div
          ref={containerRef}
          aria-label="Scroll to top"
          className={styles.containerDualSelect}
          role="button"
          style={{ gridArea: 'c' }}
          tabIndex={0}
          onClick={handleToScrollToTop}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleToScrollToTop();
            }
          }}>
          <DualSelect
            buttonText={buttonText(rangeValues[FILTER_TYPE_NAME_PRICE_RANGE])}
            lowerOptionList={[
              { text: 'Lowest', value: '' },
              ...priceRangeSelectOptionList,
            ]}
            lowerValue={rangeValues[FILTER_TYPE_NAME_PRICE_RANGE].lowerValue}
            name="Price Range"
            upperOptionList={[
              ...priceRangeSelectOptionList,
              { text: 'Highest', value: '' },
            ]}
            upperValue={rangeValues[FILTER_TYPE_NAME_PRICE_RANGE].upperValue}
            onChange={handleRangeChange}
            onClear={handleRangeClear(FILTER_TYPE_NAME_PRICE_RANGE)} />
        </div>
      )}
      {!simpleMode && (
        <div
          ref={containerRef}
          aria-label="Scroll to top"
          className={styles.containerDualSelect}
          role="button"
          style={{ gridArea: 'd' }}
          tabIndex={0}
          onClick={handleToScrollToTop}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleToScrollToTop();
            }
          }}>
          <DualSelect
            buttonText={buttonText(rangeValues.Depreciation)}
            lowerOptionList={[
              { text: 'Lowest', value: '' },
              ...depreciationRangeSelectOptionList,
            ]}
            lowerValue={rangeValues.Depreciation.lowerValue}
            name="Depreciation"
            rectCalculator={
              isIpadMiniMode
                ? alignBottomXBegin
                : alignBottomXEnd
            }
            upperOptionList={[
              ...depreciationRangeSelectOptionList,
              { text: 'Highest', value: '' },
            ]}
            upperValue={rangeValues.Depreciation.upperValue}
            onChange={handleRangeChange}
            onClear={handleRangeClear(FILTER_TYPE_NAME_DEPRECIATION)} />
        </div>
      )}
      {!simpleMode && (
        <div
          ref={containerRef}
          aria-label="Scroll to top"
          className={styles.containerDualSelect}
          role="button"
          style={{ gridArea: 'e' }}
          tabIndex={0}
          onClick={handleToScrollToTop}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleToScrollToTop();
            }
          }}>
          <SpecialSelect
            optionList={categorySelectOptionConfigs.optionList}
            selectName="Category"
            selectValues={selectValues}
            setSelectValues={setSelectValues} />
        </div>
      )}
      {selectOptions?.map(option => (
        <Select
          key={option.key}
          autoScrollToActiveItem={option?.autoScrollToActiveItem}
          className={option?.className}
          fuzzy={option?.fuzzy}
          headerClassName={option?.headerClassName}
          hiddenOptionList={option?.hiddenOptionList}
          isDisabled={option?.isDisabled}
          isInvalid={option?.isInvalid}
          isReadonly={option?.isReadonly}
          menuAlignment={option?.menuAlignment}
          menuAlignmentContainerLayer={option?.menuAlignmentContainerLayer}
          menuBorderRadius={option?.menuBorderRadius}
          menuClassName={option?.menuClassName}
          menuCloseDelayMsec={option?.menuCloseDelayMsec}
          menuContainerClassName={option?.menuContainerClassName}
          menuFit={option?.menuFit}
          menuItemTextOverflow={option?.menuItemTextOverflow}
          menuLayerPosition={option?.menuLayerPosition}
          menuOffset={option?.menuOffset}
          menuPosition={option?.menuPosition}
          optionList={option?.optionList}
          placeholder={option?.placeholder}
          size={option?.size}
          value={option?.value}
          onChange={option?.onChange} />
      ))}
      <div
        className={cx(styles.containerBtn, simpleMode && styles.isSimpleMode)}
        style={{ gridArea: 'f' }}>
        <button
          aria-label="Filter"
          className={cx(
            styles.filterButton,
            simpleMode && styles.isSimpleMode,
            (!isDesktop() && numberOfAppliedFilters > 0)
              ? styles.withNumberOfFilter
              : '',
          )}
          type="button"
          onClick={handleFilterClick}>
          <Filter size={FILTER_ICON_SIZE} />
          {simpleMode && (
            <span className={styles.textFilter}>
              {isDesktop() ? 'Filter' : ''}
            </span>
          )}
          {!isDesktop() && numberOfAppliedFilters > 0 && (
            <div className={styles.containerFilterLabel}>
              {numberOfAppliedFilters}
            </div>
          )}
        </button>
        <Button
          className={cx(
            styles.searchButton,
            simpleMode && styles.isSimpleMode,
            styles.isRound,
          )}
          disabled={submitButtonOption?.disabled}
          type="submit"
          variant="contained"
          onClick={handleSearchClick}>
          {BUTTON_SEARCH_TEXT}
        </Button>
      </div>
    </form>
  );
};
